import styled from 'styled-components';
import React from 'react';
import { focusVisibleStyles, standardTransitionStyles } from '../../sharedStyles';

export const ANIMATION_DURATION = 300;
export const CARET_SIZE = 20;

const StyledCaret = styled.button<{
  $isOpen?: boolean;
  $marginLeft?: string;
  $isCircle?: boolean;
  $animationDuration: number;
}>`
  all: unset;
  cursor: pointer;
  padding: 0 4px;
  margin-left: ${({ $marginLeft }) => $marginLeft || '0'};
  border-radius: var(--border-radius-small);
  display: flex;
  align-items: center;
  justify-content: center;
  ${standardTransitionStyles}

  ${({ $isCircle }) => {
    return $isCircle
      ? `
        min-width: ${CARET_SIZE}px;
        min-height: ${CARET_SIZE}px;
        padding: 0;
        border-radius: 50%;
        
        &:hover {
          background: var(--grayscale-50);
        }
      `
      : `
        width: auto;
        height: auto;
        background: transparent;
        
        &:hover {
          background-color: var(--grayscale-50);
        }
      `;
  }}

  svg {
    transition: transform ${({ $animationDuration }) => $animationDuration}ms ease-out;

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  }

  ${({ $isOpen }) => {
    return (
      $isOpen &&
      `
      svg {
        transform: rotate(180deg);
      }
    `
    );
  }}

  ${focusVisibleStyles}
`;

export type CaretProps = {
  /**
   * Class name for additional styling; used to pass down styles when using inheritance in styled components
   */
  className?: string;
  /**
   * Whether the caret is in an open/expanded state
   */
  isOpen?: boolean;
  /**
   * Optional margin-left value
   */
  $marginLeft?: string;
  /**
   * Whether the caret should be rendered as a circle (default: true)
   */
  isCircle?: boolean;
  /**
   * Optional animation duration
   */
  animationDuration?: number;
  /**
   * Click handler for the caret
   */
  onClick?: (e: React.MouseEvent) => void;
  /**
   * Children elements (typically an SVG icon)
   */
  children: React.ReactNode;
};

/**
 * A clickable caret component that can rotate an icon to indicate open/closed state.
 */
export const Caret = ({
  className,
  isOpen,
  $marginLeft,
  isCircle = true,
  animationDuration = ANIMATION_DURATION,
  onClick,
  children,
  ...rest
}: CaretProps) => {
  return (
    <StyledCaret
      className={className}
      $isOpen={isOpen}
      $marginLeft={$marginLeft}
      $isCircle={isCircle}
      $animationDuration={animationDuration}
      onClick={onClick}
      {...rest}
    >
      {children}
    </StyledCaret>
  );
};

export default Caret;
